import * as React from "react";
import { DefaultButton, getId, MessageBar, MessageBarButton, MessageBarType, PrimaryButton, ProgressIndicator, Spinner, Stack, Text } from "@fluentui/react";
import { LanguageContext } from "../../../../utilities/LocalizationModule";
import { storedCopySave, StoredCopyStep, StoredCopyChoice, IStoredCopySaveState } from "../../services/storedCopySave";

// The part of the save's state that select picks: the component re-renders only when that changes, so
// one that follows the step isn't re-rendered on every progress report. (React 16's useState still
// renders once for an unchanged value, hence the comparison with what was rendered.)
export const useStoredCopySave = <T,>(select: (state: IStoredCopySaveState) => T): T => {
  const [, rerender] = React.useReducer((renders: number) => renders + 1, 0);
  const value = select(storedCopySave.getState());
  const rendered = React.useRef(value);
  rendered.current = value;
  React.useEffect(() => {
    const onChange = () => {
      if (select(storedCopySave.getState()) !== rendered.current) {
        rerender();
      }
    };
    // It may have changed between the render and this subscription.
    onChange();
    return storedCopySave.subscribe(onChange);
  }, []);
  return value;
};

// Keeps keyboard and screen-reader users in place: when the focused element goes (an answered question
// gives way to progress), focus moves to target rather than falling to the page. Checked when key changes.
export const useRefocusWhenLost = (target: React.RefObject<HTMLElement>, key: unknown): void => {
  React.useEffect(() => {
    const focused = document.activeElement;
    if (target.current && (!focused || focused === document.body)) {
      target.current.focus();
    }
  }, [key]);
};

// The language the text is in (index.tsx picks it the same way): languages without strings get English.
const locale = (): string => {
  const language = (typeof Office !== "undefined" && Office.context?.displayLanguage) || navigator.language;
  return /^(en|nl|de)\b/i.test(language) ? language : "en";
};

const formatTime = (date: Date): string => {
  const isToday = date.toDateString() === new Date().toDateString();
  return new Intl.DateTimeFormat(locale(), isToday ? { hour: "2-digit", minute: "2-digit" } : { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }).format(date);
};

// The user's clock can run behind the server's: a copy "from the future" was stored just now.
const formatAgo = (date: Date): string => {
  const seconds = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000));
  const relative = new Intl.RelativeTimeFormat(locale(), { numeric: "auto" });
  if (seconds < 60) {
    return relative.format(-seconds, "second");
  }
  if (seconds < 3600) {
    return relative.format(-Math.floor(seconds / 60), "minute");
  }
  if (seconds < 86400) {
    return relative.format(-Math.floor(seconds / 3600), "hour");
  }
  return relative.format(-Math.floor(seconds / 86400), "day");
};

// Both sizes in the total's unit: KB below 1 MB, so a small document doesn't read "0.0 of 0.0 MB".
const formatSizes = (done: number, total: number): { done: string; total: string } => {
  const isSmall = total < 1024 * 1024;
  const format = new Intl.NumberFormat(locale(), isSmall ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const inUnit = (bytes: number) => format.format(bytes / (isSmall ? 1024 : 1024 * 1024));
  return { done: inUnit(done), total: `${inUnit(total)} ${isSmall ? "KB" : "MB"}` };
};

const answer = (choice: StoredCopyChoice) => () => storedCopySave.answer(choice);

// How often the question's "N minutes ago" is brought up to date.
const AGO_REFRESH_MS = 30_000;

// An encrypted document saved from its stored copy in Office for the web (see storedCopySave): the
// choice when the copy may miss recent edits, the wait for a newer copy, transfer progress, and
// "taking longer than expected" with Keep waiting. Cancel is on offer at the questions and, while
// downloading, once that message shows; a short download doesn't need it.
export const StoredCopySaveStatus = () => {
  const languageStrings = React.useContext(LanguageContext);
  const state = useStoredCopySave(current => current);
  const [textId] = React.useState(() => getId("storedCopyText"));
  const [, refresh] = React.useReducer((refreshes: number) => refreshes + 1, 0);

  React.useEffect(() => {
    if (state.step !== StoredCopyStep.Choice) {
      return undefined;
    }
    const timer = setInterval(refresh, AGO_REFRESH_MS);
    return () => clearInterval(timer);
  }, [state.step]);

  // The question appears in place of the progress and focus goes to its answer, which carries the
  // question's text: an alert would be read out again each time "N minutes ago" changes. The same goes
  // for the waiting text.
  switch (state.step) {
    case StoredCopyStep.Choice:
      return (
        <Stack tokens={{ childrenGap: 16 }}>
          <Text id={textId}>{languageStrings.OfficeFileStoredCopyChoice.replace("{time}", formatTime(state.storedAt)).replace("{ago}", formatAgo(state.storedAt))}</Text>
          <PrimaryButton autoFocus aria-describedby={textId} text={languageStrings.OfficeFileStoredCopySaveThisCopy} onClick={answer(StoredCopyChoice.SaveStoredCopy)} />
          <DefaultButton text={languageStrings.OfficeFileStoredCopyWaitForChanges} onClick={answer(StoredCopyChoice.WaitForChanges)} />
          <DefaultButton text={languageStrings.Cancel} onClick={storedCopySave.cancel} />
        </Stack>
      );

    case StoredCopyStep.WaitingForChanges:
      return (
        <Stack tokens={{ childrenGap: 16 }}>
          <Spinner />
          <Text id={textId}>{languageStrings.OfficeFileStoredCopyWaiting.replace("{time}", formatTime(state.storedAt))}</Text>
          <PrimaryButton autoFocus aria-describedby={textId} text={languageStrings.OfficeFileStoredCopySaveNow} onClick={answer(StoredCopyChoice.SaveStoredCopy)} />
          <DefaultButton text={languageStrings.Cancel} onClick={storedCopySave.cancel} />
        </Stack>
      );

    case StoredCopyStep.Downloading:
    case StoredCopyStep.Uploading: {
      const isDownloading = state.step === StoredCopyStep.Downloading;
      // Uploaded to Docubird: the API still stores the file in SharePoint or OneDrive (seconds for large files).
      const isFinishing = !isDownloading && state.total > 0 && state.transferred >= state.total;
      const sizes = formatSizes(state.transferred, state.total);
      const label = isFinishing
        ? languageStrings.OfficeFileStoredCopyFinishing
        : (isDownloading ? languageStrings.OfficeFileStoredCopyDownloading : languageStrings.OfficeFileStoredCopyUploading)
          .replace("{done}", sizes.done)
          .replace("{total}", sizes.total);
      return (
        <Stack tokens={{ childrenGap: 16 }}>
          <ProgressIndicator label={label} percentComplete={!isFinishing && state.total ? state.transferred / state.total : undefined} />
          {state.isSlow && (
            <MessageBar
              messageBarType={MessageBarType.warning}
              isMultiline
              actions={
                <div>
                  <MessageBarButton onClick={storedCopySave.keepWaiting}>{languageStrings.OfficeFileStoredCopyKeepWaiting}</MessageBarButton>
                  {isDownloading && <MessageBarButton onClick={storedCopySave.cancel}>{languageStrings.Cancel}</MessageBarButton>}
                </div>
              }
            >
              {isDownloading ? languageStrings.OfficeFileStoredCopyTakingLonger : languageStrings.OfficeFileStoredCopyTakingLongerUploading}
            </MessageBar>
          )}
        </Stack>
      );
    }

    default:
      return null;
  }
};
